// ui/presenters/auditRunTrailPresenter.js
import { i18n } from 'boot/i18n';

import { VERDICTS } from '../../domain/constants/auditResults';
import { AUDIT_RUN_TRAIL_FIELD } from '../../domain/constants/auditRunTrail';
import { AUDIT_TRIGGERS } from '../../domain/constants/auditTriggers';

/** @typedef {import('../../domain/entities/AuditRunTrail.types').AuditRunOutcome} AuditRunOutcome */
/** @typedef {import('../../domain/entities/AuditRunTrail.types').AuditRunTrailEntry} AuditRunTrailEntry */
/** @typedef {import('src/utils/AuditLogUtils').AuditTrailEvent} AuditTrailEvent */

// A backend newer than this client may write a trigger or outcome it has no wording for yet.
const labelOrValue = (key, value) => (i18n.te(key) ? i18n.t(key) : value);

/**
 * @param {AuditRunTrailEntry['overrideValue']} overrideValue
 * @return {string|undefined}
 */
function runKey({ trigger, isRerun }) {
  if (trigger !== AUDIT_TRIGGERS.CHECKOUT) {
    return trigger;
  }

  if (typeof isRerun !== 'boolean') {
    return 'checkoutUnknown';
  }

  return isRerun ? 'checkoutRerun' : 'checkout';
} // runKey()

/**
 * @param {AuditRunOutcome} outcome
 * @return {string}
 */
export function auditRunOutcomeLabel(outcome) {
  return labelOrValue(`noteAuditor.auditTrail.outcomes.${outcome}`, outcome);
}

/**
 * @return {string}
 */
export function auditRunEventType() {
  return i18n.t('noteAuditor.auditTrail.eventType');
}

/**
 * @param {AuditRunTrailEntry} entry
 * @return {string}
 */
export function describeAuditRun(entry) {
  const overrideValue = entry.overrideValue ?? {};
  const { trigger, strictness } = overrideValue;
  const isVerdict = Object.values(VERDICTS).includes(entry.newValue);

  return i18n.t(`noteAuditor.auditTrail.${isVerdict ? 'description' : 'descriptionWithoutVerdict'}`, {
    run: labelOrValue(`noteAuditor.auditTrail.runs.${runKey(overrideValue)}`, trigger),
    strictness: labelOrValue(`noteAuditor.strictnessModes.${strictness}`, strictness),
    outcome: auditRunOutcomeLabel(entry.newValue),
  });
}

/** @type {AuditTrailEvent} */
export const noteAuditRunTrailEvent = {
  kind: 'noteAuditRun',
  collection: 'sessionActivities',
  field: AUDIT_RUN_TRAIL_FIELD,
  label: auditRunEventType,
  describe: describeAuditRun,
  formatValue: auditRunOutcomeLabel,
};
